import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { apiRequest, API_BASE_URL } from "../../services/apiClient";
import { useLanguage } from "../../i18n/LanguageContext";

function getResetToken() {
  try {
    const params = new URLSearchParams(window.location.search || "");
    return String(params.get("token") || params.get("resetToken") || "").trim();
  } catch {
    return "";
  }
}

export default function ResetPasswordPage({ token: tokenProp, onContinue }) {
  const { t } = useLanguage();
  const [visualHeight, setVisualHeight] = useState(() => {
    if (typeof window === "undefined") return 800;
    return window.visualViewport?.height || window.innerHeight;
  });
  const formRef = useRef(null);
  const token = useMemo(() => String(tokenProp || getResetToken() || "").trim(), [tokenProp]);
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const keepFocusedFieldVisible = useCallback(() => {
    window.setTimeout(() => {
      const activeElement = document.activeElement;
      if (!activeElement || !formRef.current?.contains(activeElement)) return;
      activeElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  }, []);

  useEffect(() => {
    const viewport = window.visualViewport;
    const updateViewport = () => {
      setVisualHeight(viewport?.height || window.innerHeight);
      keepFocusedFieldVisible();
    };

    updateViewport();
    viewport?.addEventListener("resize", updateViewport);
    viewport?.addEventListener("scroll", updateViewport);
    window.addEventListener("resize", updateViewport);

    return () => {
      viewport?.removeEventListener("resize", updateViewport);
      viewport?.removeEventListener("scroll", updateViewport);
      window.removeEventListener("resize", updateViewport);
    };
  }, [keepFocusedFieldVisible]);

  useEffect(() => {
    let alive = true;
    const validate = async () => {
      setValidating(true);
      setError("");
      try {
        if (!token || token.length < 32) throw new Error(t("auth.resetLinkInvalid"));
        await apiRequest(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`);
        if (!alive) return;
        setValid(true);
      } catch (err) {
        if (!alive) return;
        setValid(false);
        setError(err?.message || t("auth.resetLinkInvalid"));
      } finally {
        if (alive) setValidating(false);
      }
    };
    validate();
    return () => { alive = false; };
  }, [token, t]);

  const goLogin = () => {
    try { window.history.replaceState({}, "", "/"); } catch {}
    if (Capacitor.isNativePlatform() && typeof onContinue === "function") {
      onContinue();
      return;
    }
    // Web ekranındayken farklı hosttaki doğrulanmış Universal/App Link'e git.
    // Uygulama yüklüyse native uygulama açılır; değilse backend tekrar web
    // ana sayfasına yönlendirir.
    window.location.assign(`${API_BASE_URL}/auth/deep-link/open`);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (password.length < 8 || password.length > 128) {
      setError(t("auth.resetPasswordMin"));
      return;
    }
    if (password !== password2) {
      setError(t("auth.resetPasswordMismatch"));
      return;
    }
    try {
      setLoading(true);
      await apiRequest("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, newPassword: password }),
      });
      setSuccess(true);
      setValid(false);
      try { window.history.replaceState({}, "", "/"); } catch {}
    } catch (err) {
      setError(err?.message || t("auth.resetPasswordFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="authPage2 resetPasswordContainer"
      style={{ "--reset-visual-height": visualHeight + "px" }}
    >
      <div className="authScreen resetPasswordScreen">
        <div className="authHero resetPasswordHero">
          <div className="authHeroLogo">
            <img src="/logo512.png" alt={t("app.name")} className="authHeroLogoImage" />
          </div>
          <h1 className="authHeroTitle">{t("auth.resetPasswordTitle")}</h1>
          <p className="authHeroSubtitle">{t("auth.resetPasswordSubtitle")}</p>
        </div>

        <form
          ref={formRef}
          className="authFormCard resetPasswordCard"
          onSubmit={submit}
          onFocusCapture={keepFocusedFieldVisible}
        >
          <div className="authFormCardGlow" />

          {validating ? (
            <div className="resetPasswordStateBox">
              <div className="resetPasswordSpinner" />
              <h2>{t("auth.resetLinkChecking")}</h2>
              <p>{t("auth.resetLinkCheckingSub")}</p>
            </div>
          ) : success ? (
            <div className="resetPasswordStateBox">
              <div className="resetPasswordSuccessIcon">✓</div>
              <h2>{t("auth.resetPasswordSuccessTitle")}</h2>
              <p>{t("auth.resetPasswordSuccessSub")}</p>
              <button type="button" className="authLoginBtn" onClick={goLogin}>{t("auth.backToLogin")}</button>
            </div>
          ) : !valid ? (
            <div className="resetPasswordStateBox">
              <div className="resetPasswordErrorIcon">!</div>
              <h2>{t("auth.resetLinkInvalidTitle")}</h2>
              <p>{error || t("auth.resetLinkInvalid")}</p>
              <button type="button" className="authLoginBtn" onClick={goLogin}>{t("auth.backToLogin")}</button>
            </div>
          ) : (
            <>
              <h2 className="authFormTitle">{t("auth.createNewPassword")}</h2>
              <p className="authFormSub">{t("auth.createNewPasswordSub")}</p>

              <div className="authInputGroup2 authPasswordGroup">
                <div className="authInputIcon2">🔒</div>
                <input
                  className="authInputNew2"
                  type={showPassword ? "text" : "password"}
                  minLength={8}
                  maxLength={128}
                  placeholder={t("auth.placeholder.newPassword")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button type="button" className="authPasswordToggle" onClick={() => setShowPassword((v) => !v)}>
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>

              <div className="authInputGroup2 authPasswordGroup">
                <div className="authInputIcon2">🔐</div>
                <input
                  className="authInputNew2"
                  type={showPassword ? "text" : "password"}
                  minLength={8}
                  maxLength={128}
                  placeholder={t("auth.placeholder.confirmNewPassword")}
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  autoComplete="new-password"
                />
              </div>

              {error && <div className="authErrorBox2">⚠️ {error}</div>}

              <button type="submit" disabled={loading} className="authLoginBtn">
                {loading ? (
                  <span className="authBtnLoading"><span className="authDot" /><span className="authDot" /><span className="authDot" /></span>
                ) : (
                  <span>{t("auth.saveNewPassword")} <span className="authBtnArrow">→</span></span>
                )}
              </button>
              <button type="button" className="authSecondaryActionBtn" onClick={goLogin}>{t("auth.backToLogin")}</button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
